import { useCustomTokens } from '@sushiswap/hooks'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { logger } from 'src/lib/logger'
import { getIdFromChainIdAddress } from 'sushi'
import {
  type EvmAddress,
  type EvmChainId,
  EvmToken,
  isEvmAddress,
  isEvmChainId,
} from 'sushi/evm'
import { useConfig } from 'wagmi'
import { getToken } from '../../actions/getToken'
import { fetchKatanaTokenList } from '../../components/token-selector/hooks/katana-token-list'
import { searchSubgraphToken } from '../../components/token-selector/hooks/search-subgraph-token'
import type { PublicWagmiConfig } from '../../config/public'

async function getEvmToken(
  chainId: EvmChainId,
  address: EvmAddress,
  config: PublicWagmiConfig,
) {
  const resp = await getToken(config, {
    address,
    chainId,
  })
  const { decimals, address: tokenAddress, symbol, name } = resp

  return new EvmToken({
    chainId,
    address: tokenAddress,
    name: name || '',
    symbol: symbol || '',
    decimals,
    metadata: {
      approved: false,
    },
  })
}

interface GetTokenWithQueryCacheFn<TChainId extends EvmChainId> {
  chainId: TChainId | undefined
  address: AddressFor<TChainId> | undefined | null
  customTokens: Record<string, TokenFor<EvmChainId, { approved: boolean }>>
  hasToken: (cur: string | TokenFor<TChainId>) => boolean
  config: PublicWagmiConfig
}

export async function getTokenWithCacheQueryFn<TChainId extends EvmChainId>({
  chainId,
  address,
  customTokens,
  hasToken,
  config,
}: GetTokenWithQueryCacheFn<TChainId>): Promise<
  TokenFor<TChainId, { approved: boolean }>
> {
  if (!chainId) {
    throw Error('chainId is required')
  }

  if (!address) {
    throw Error('address is required')
  }

  // Try fetching from localStorage
  if (hasToken(getIdFromChainIdAddress(chainId, address))) {
    return customTokens[getIdFromChainIdAddress(chainId, address)] as TokenFor<
      TChainId,
      { approved: boolean }
    >
  }

  // Look up in Katana token list
  try {
    const tokenList = await fetchKatanaTokenList()
    const match = tokenList.find(
      (t) => t.address.toLowerCase() === address.toLowerCase(),
    )
    if (match && isEvmChainId(chainId)) {
      return new EvmToken({
        chainId,
        address: match.address as EvmAddress,
        name: match.name,
        symbol: match.symbol,
        decimals: match.decimals,
        metadata: { approved: match.approved },
      }) as TokenFor<TChainId, { approved: boolean }>
    }
  } catch (error) {
    logger.error(error, {
      location: 'useTokenWithCache',
      action: 'fetchKatanaTokenList',
      token_chain_id: chainId,
      token_address: address,
    })
  }

  // Try V3 subgraph
  if (isEvmChainId(chainId) && isEvmAddress(address)) {
    try {
      const subgraphToken = await searchSubgraphToken(address as EvmAddress)
      if (subgraphToken) {
        return new EvmToken({
          chainId,
          address: subgraphToken.address as EvmAddress,
          name: subgraphToken.name,
          symbol: subgraphToken.symbol,
          decimals: subgraphToken.decimals,
          metadata: { approved: false },
        }) as TokenFor<TChainId, { approved: boolean }>
      }
    } catch (error) {
      logger.error(error, {
        location: 'useTokenWithCache',
        action: 'searchSubgraphToken',
        token_chain_id: chainId,
        token_address: address,
      })
    }
  }

  try {
    if (isEvmChainId(chainId)) {
      return (await getEvmToken(
        chainId,
        address as EvmAddress,
        config,
      )) as TokenFor<TChainId, { approved: boolean }>
    }
  } catch (error) {
    logger.error(error, {
      location: 'useTokenWithCache',
      action: 'fetchTokenRpc',
      token_chain_id: chainId,
      token_address: address,
    })
  }

  throw Error('Could not fetch token')
}

interface UseTokenParams<TChainId extends EvmChainId> {
  chainId: TChainId | undefined
  address: AddressFor<TChainId> | undefined
  enabled?: boolean
  keepPreviousData?: boolean
}

type UseTokenReturn<TChainId extends EvmChainId> = TokenFor<TChainId>

export function getTokenWithQueryCacheHydrate<TChainId extends EvmChainId>(
  chainId: TChainId | undefined,
  data: TokenFor<TChainId> | undefined,
): UseTokenReturn<TChainId> | undefined {
  if (data && chainId) {
    return data
  }

  return undefined
}

export function useTokenWithCache<TChainId extends EvmChainId>({
  chainId,
  address,
  enabled = true,
  keepPreviousData: isKeepPreviousData = true,
}: UseTokenParams<TChainId>) {
  const { data: customTokens, hasToken } = useCustomTokens()
  const select = useCallback(
    (data: TokenFor<TChainId, { approved: boolean }>) =>
      getTokenWithQueryCacheHydrate(chainId, data),
    [chainId],
  )

  const config = useConfig()

  return useQuery({
    queryKey: ['token', { chainId, address }],
    queryFn: async () =>
      getTokenWithCacheQueryFn({
        chainId,
        address,
        customTokens,
        hasToken,
        config,
      }),
    enabled: Boolean(enabled && chainId && address && isEvmAddress(address)),
    select,
    placeholderData: isKeepPreviousData ? keepPreviousData : undefined,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 900000, // 15 mins
    gcTime: 86400000, // 24hs
  })
}
